import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import QRCode from 'qrcode';
import { Coffee, Loader2, Download, Clock } from 'lucide-react';
import { useIdentity } from '@core/contexts/IdentityContext';
import { useProfile } from '@core/contexts/ProfileContext';
import { Header } from '@core/components/Header';
import { Card } from '@core/components/Card';
import { getBundleQR, getBundleHistory, type BundleQRData, type BundleUsage } from '@core/api/bundles';

// QR абонемента — общий экран обоих входов (раньше жил в мини-аппе и брал клиента из Telegram, поэтому в PWA
// его не было). Клиент — из сессии входа (K-002); Telegram ID уходит только как необязательная сверка.
export function BundleQRPage() {
  const { id } = useParams<{ id: string }>();
  const { telegramId } = useIdentity();
  const { user, loading: profileLoading } = useProfile();
  const userId = user?.id ?? null;

  const [data, setData] = useState<BundleQRData | null>(null);
  const [history, setHistory] = useState<BundleUsage[]>([]);
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    if (!userId || !id) {
      if (!profileLoading) setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const [qrData, usageHistory] = await Promise.all([
        getBundleQR(id, telegramId),
        getBundleHistory(id, telegramId),
      ]);
      setData(qrData);
      setHistory(usageHistory);

      // Generate QR code
      if (qrData.canShowQR && qrData.barcode) {
        const url = await QRCode.toDataURL(qrData.barcode, {
          width: 280,
          margin: 2,
          color: { dark: '#000000', light: '#ffffff' },
        });
        setQrDataUrl(url);
      }
    } catch (err) {
      console.error('[BundleQRPage] Failed to load:', err);
    } finally {
      setLoading(false);
    }
  }, [userId, telegramId, id, profileLoading]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  // Poll for status changes (PENDING → ACTIVE)
  useEffect(() => {
    if (!data || data.status !== 'PENDING' || !userId || !id) return;

    const interval = setInterval(async () => {
      try {
        const updated = await getBundleQR(id, telegramId);
        if (updated.status !== 'PENDING') {
          setData(updated);
          if (updated.canShowQR && updated.barcode) {
            const url = await QRCode.toDataURL(updated.barcode, {
              width: 280,
              margin: 2,
              color: { dark: '#000000', light: '#ffffff' },
            });
            setQrDataUrl(url);
          }
        }
      } catch {
        // Silent polling failure
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [data?.status, userId, telegramId, id]);

  const handleDownload = () => {
    if (!qrDataUrl || !data) return;
    const link = document.createElement('a');
    link.download = `bundle-${data.planName}.png`;
    link.href = qrDataUrl;
    link.click();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-page text-text-main">
        <Header title="Абонемент" />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="animate-spin text-primary" size={32} />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-page text-text-main">
        <Header title="Абонемент" />
        <div className="text-center py-12">
          <p className="text-text-muted">Абонемент не найден</p>
        </div>
      </div>
    );
  }

  const used = data.totalCount - data.remainingCount;
  const progress = data.totalCount > 0 ? (used / data.totalCount) * 100 : 0;
  const daysLeft = data.expiresAt
    ? Math.max(0, Math.ceil((new Date(data.expiresAt).getTime() - Date.now()) / (24 * 60 * 60 * 1000)))
    : null;

  return (
    <div className="min-h-screen bg-page text-text-main pb-20">
      <Header title={data.planName} />

      <div className="p-4 space-y-4">
        {/* QR Code */}
        <Card className="text-center space-y-4">
          {data.canShowQR && qrDataUrl ? (
            <>
              <div className="inline-block p-3 bg-white rounded-2xl shadow-sm">
                <img src={qrDataUrl} alt="QR Code" className="w-56 h-56 mx-auto" />
              </div>

              <div>
                <p className="text-xs text-text-muted font-mono tracking-wider">
                  {data.barcode}
                </p>
                <p className="text-sm text-text-muted mt-1">
                  {data.qrType === 'purchase'
                    ? 'Покажите QR кассиру для оплаты'
                    : 'Покажите QR кассиру для использования'}
                </p>
              </div>

              <button
                onClick={handleDownload}
                className="inline-flex items-center gap-2 text-sm text-primary font-medium"
              >
                <Download size={16} />
                Скачать QR
              </button>
            </>
          ) : (
            <div className="py-8">
              <Coffee className="w-12 h-12 mx-auto text-text-muted opacity-50 mb-3" />
              <p className="text-text-muted">
                {data.status === 'COMPLETED'
                  ? 'Все напитки использованы'
                  : data.status === 'EXPIRED'
                  ? 'Абонемент истёк'
                  : data.status === 'CANCELLED'
                  ? 'Абонемент отменён'
                  : 'QR-код недоступен'}
              </p>
            </div>
          )}
        </Card>

        {/* Status info */}
        <Card className="space-y-3">
          <h3 className="font-bold text-text-main">Информация</h3>

          {data.status === 'PENDING' && (
            <div className="text-sm text-yellow-700 bg-yellow-50 rounded-xl p-3">
              Ожидает оплаты на кассе. QR-код обновится автоматически после оплаты.
            </div>
          )}

          {data.status === 'ACTIVE' && (
            <>
              {/* Progress */}
              <div className="space-y-1">
                <div className="flex justify-between text-sm text-text-muted">
                  <span>Использовано: {used} из {data.totalCount}</span>
                  <span>{data.remainingCount} осталось</span>
                </div>
                <div className="h-2.5 bg-secondary/20 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all duration-500 rounded-full"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>

              {daysLeft !== null && (
                <div className="flex items-center gap-2 text-sm text-text-muted">
                  <Clock size={14} />
                  <span>
                    {daysLeft > 0
                      ? `Действует ещё ${daysLeft} ${daysLeft === 1 ? 'день' : daysLeft < 5 ? 'дня' : 'дней'}`
                      : 'Истекает сегодня'}
                  </span>
                </div>
              )}
            </>
          )}
        </Card>

        {/* Usage history */}
        {history.length > 0 && (
          <Card className="space-y-3">
            <h3 className="font-bold text-text-main">Использование</h3>
            <div className="space-y-2">
              {history.map((usage) => (
                <div
                  key={usage.id}
                  className="flex items-center justify-between text-sm py-1.5 border-b border-border last:border-0"
                >
                  <div className="flex items-center gap-2">
                    <Coffee size={14} className="text-primary" />
                    <span className="text-text-main">{usage.rewardName}</span>
                  </div>
                  <span className="text-xs text-text-muted">
                    {new Date(usage.createdAt).toLocaleDateString('ru-RU', {
                      day: 'numeric',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
